import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import {
  Container,
  Paper,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(3) },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  paper: { padding: theme.spacing(2) },
  empty: { padding: theme.spacing(6), textAlign: "center", color: "#999" },
  logEntry: {
    borderLeft: "3px solid #1976d2",
    paddingLeft: 8,
    marginBottom: 8
  },
  logError: { borderLeftColor: "#c62828" },
  logSkipped: { borderLeftColor: "#f9a825" },
  pre: {
    fontSize: 11,
    background: "#f5f5f5",
    padding: 6,
    borderRadius: 4,
    overflow: "auto",
    margin: "4px 0 0"
  }
}));

const STATUS_COLOR = {
  running: { background: "#e3f2fd", color: "#0d47a1" },
  waiting: { background: "#fff8e1", color: "#827717" },
  completed: { background: "#e8f5e9", color: "#1b5e20" },
  failed: { background: "#ffebee", color: "#b71c1c" },
  transferred_to_human: { background: "#f3e5f5", color: "#4a148c" }
};

const AiFlowExecutions = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const [flow, setFlow] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openExecution, setOpenExecution] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [flowRes, execRes] = await Promise.all([
          api.get(`/ai-flows/${id}`),
          api.get(`/ai-flows/${id}/executions`)
        ]);
        if (!active) return;
        setFlow(flowRes.data);
        setItems(execRes.data || []);
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const openLogs = async exec => {
    setOpenLoading(true);
    setOpenExecution({ id: exec.id, loading: true });
    try {
      const { data } = await api.get(`/ai-flow-executions/${exec.id}`);
      setOpenExecution(data);
    } catch (err) {
      toastError(err);
      setOpenExecution(null);
    } finally {
      setOpenLoading(false);
    }
  };

  return (
    <Container className={classes.root} maxWidth="lg">
      <div className={classes.header}>
        <IconButton onClick={() => history.push("/ai-flows")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          Execucoes — {flow?.name || "..."}
        </Typography>
      </div>

      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.empty}>
            <CircularProgress />
          </div>
        ) : items.length === 0 ? (
          <div className={classes.empty}>
            Nenhuma execucao registrada ainda.
          </div>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Contato</TableCell>
                  <TableCell>Inicio</TableCell>
                  <TableCell>Fim</TableCell>
                  <TableCell align="right">Logs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(ex => (
                  <TableRow key={ex.id} hover>
                    <TableCell>{ex.id}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ex.status}
                        style={STATUS_COLOR[ex.status] || {}}
                      />
                    </TableCell>
                    <TableCell>
                      {ex.Ticket ? `#${ex.Ticket.id}` : ex.ticketId || "—"}
                    </TableCell>
                    <TableCell>
                      {ex.Ticket?.contact
                        ? `${ex.Ticket.contact.name} (${ex.Ticket.contact.number})`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {ex.startedAt
                        ? new Date(ex.startedAt).toLocaleString("pt-BR")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {ex.finishedAt
                        ? new Date(ex.finishedAt).toLocaleString("pt-BR")
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openLogs(ex)}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={!!openExecution}
        onClose={() => setOpenExecution(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Logs da execucao #{openExecution?.id}</DialogTitle>
        <DialogContent>
          {openLoading ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <CircularProgress />
            </div>
          ) : !openExecution?.logs ? (
            <div className={classes.empty}>Sem logs.</div>
          ) : (
            <>
              <Typography variant="caption" color="textSecondary">
                Status final:{" "}
                <Chip
                  size="small"
                  label={openExecution.status}
                  style={STATUS_COLOR[openExecution.status] || {}}
                />
              </Typography>
              <Divider style={{ margin: "12px 0" }} />
              {openExecution.logs.map((log, i) => (
                <div
                  key={log.id}
                  className={`${classes.logEntry} ${
                    log.status === "error"
                      ? classes.logError
                      : log.status === "skipped"
                      ? classes.logSkipped
                      : ""
                  }`}
                >
                  <div style={{ fontSize: 13 }}>
                    <strong>
                      {i + 1}. {log.nodeType}
                    </strong>{" "}
                    {log.status === "skipped" && (
                      <Chip
                        size="small"
                        label="pulado"
                        style={{
                          background: "#fff8e1",
                          color: "#827717",
                          height: 18,
                          fontSize: 10,
                          fontWeight: 600
                        }}
                      />
                    )}{" "}
                    <span style={{ color: "#999", fontSize: 11 }}>
                      ({log.durationMs ?? 0}ms){" "}
                      {log.createdAt
                        ? new Date(log.createdAt).toLocaleString("pt-BR")
                        : ""}
                    </span>
                  </div>
                  {log.outputJson && (
                    <pre className={classes.pre}>
                      {JSON.stringify(log.outputJson, null, 2)}
                    </pre>
                  )}
                  {log.errorMessage && (
                    <div style={{ color: "#c62828", fontSize: 12, marginTop: 4 }}>
                      {log.errorMessage}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExecution(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AiFlowExecutions;
